// La Mine aux briques (REFONTE 19/07) : la SCÈNE jouable. Un mini-contrôleur calqué sur startSolo,
// SANS vies / mort / victoire. Le coureur MARCHE dans une salle « grotte » sur un SOL DE BRIQUES
// PLAT et creuse ses cases une à une À LA PIOCHE (mécanique NORMALE du moteur, comme dans le jeu).
// Chaque brique du sol donne un loot : au creusage réussi, on GÈLE la sim, on casse une brique du
// stock via l'API Lot B, on joue la GRANDE RÉVÉLATION cinématique (loot en très grand, lumière de
// rareté, flottement, particules) puis on REBOUCHE la case (le sol reste plat, re-creusable). Une
// boutique DOM permet d'acheter des briques et des cosmétiques (éclats). TOUT est hors simulation :
// la salle est un GameState local, la casse un appel réseau (tire-et-oublie, jamais bloquant), les
// overlays du DOM. Aucune modif du moteur (sim/state), du rendu coureur (drawRunner) ni de rooms/prefs.
import { BRICK_COST, DEPTH_COST } from '../shared/economy'
import { blitPet } from './pet-papier'
import { SHARD_COST } from '../shared/loot-draw'
import { levelFromAscii } from '../shared/level'
import { LOOTS } from '../shared/loots'
import { centerOf, createGame, GameEvent, GameState, Runner } from '../shared/state'
import { COLS, colOf, PlayerInput, ROWS, rowOf, SUB } from '../shared/types'
import type { CrackResponse, PityDto, WalletDto } from '../shared/api-types'
import { apiBuyBricks, apiCrack, apiMineState, apiShardBuy } from './api'
import { crackFanfare, crackStrike, isMuted, petCue, toggleMute } from './audio'
import {
  CARD_MS, floatY, GROW_MS, isMega, pickaxeVariantOf, rarityColor, rarityRays, rarityShards, raySpin,
  revealDarken, revealFlash, revealScale, revealSway,
} from './mine-crack'
import { showToast } from './economy-ui'
import { closeEmoteWheel, handleEmoteKey, openEmoteWheel, stopEmote } from './emote-ui'
import { startVestiaire } from './vestiaire-ui'
import { drawBaroudeur } from './baroudeur'
import { equipped, titleLabel } from './loadout'
import { hueIndex } from '../shared/skins'
import { avatarHue } from './prefs'
import { createKeyboard } from './input'
import { LoopHandle, startLoop } from './loop'
import { mineRows } from './mine-scene'
import { isTouch } from './platform'
import { setRunnerLooks, DIG_ANIM_TICKS } from './render'
import { renderDepthBanner, renderMine, renderMineHud, renderSuspense, setMineDrama, type MineCam } from './mine-render'
import {
  MINE_CAM_Y, MINE_ZOOM, camYForDepth, coinRainPlan, coinRainStep, depthOfRow, mineCamStep,
  implosionP, mineCamTargetX, pickHitsDone, suspenseBurstAt, suspenseCrackStage, suspenseGlow, suspenseOver, suspensePalier, suspensePunchCam,
  suspenseShake, suspenseSkipTR, suspenseZoom, worldToScreen, type RainCoin, type SuspenseClock,
} from './mine-suspense'
import { burstBoom, coinPing, implosionWhoosh, mineAudioTeardown, mineAudioUnlock, mineCreak, palierTick, rumbleSet, rumbleStart, rumbleStop } from './mine-audio'
import {
  BRIQUE_MYSTERE, DECO_STATUE, GOLD, petsSheet, PIOCHE_FOREUSE, PIOCHE_MARTEAU,
  type SpriteRef,
} from './sprites'
import { createTouchControls } from './touch'

export interface MineOpts {
  canvas: HTMLCanvasElement
  sheet: HTMLImageElement
  onQuit: () => void
}
export interface MineHandle { stop(): void }

// Couleur + libellé par rareté (carte de révélation + boutique d'éclats). Miroirs de Rarity.
const RARITY_COLOR: Record<string, string> = { courant: '#c9c4de', rare: '#5db4ff', epique: '#b98bff', mega: '#ffd23f' }
const RARITY_LABEL: Record<string, string> = { courant: 'Courant', rare: 'Rare', epique: 'Épique', mega: 'Méga' }

export function startMine(opts: MineOpts): MineHandle {
  const { canvas, sheet } = opts
  const ctx = canvas.getContext('2d')!
  ctx.imageSmoothingEnabled = false
  setRunnerLooks(null) // solo : les prefs locales (skin/teinte) font foi pour le rendu du coureur
  const TS = canvas.width / COLS // 48 px/case MONDE (sert à la barre du bas et aux ancres écran de la révélation)

  // Dash ACTIF dans la Mine (Lot D) : capacité de mouvement (Maj / bouton tactile), l'input est déjà
  // fusionné dans mergedInput(). Pas de danger ici (ni moine ni mort) : le dash sert au plaisir de
  // franchir la salle et les trous creusés. Recharge auto 8 s (comportement standard, cf. shared/sim).
  const state = createGame(levelFromAscii('mine', mineRows()), { mode: 'solo', seed: 0, monksEnabled: false, dashEnabled: true })

  // Caméra premium (refonte 21/07) : zoom x3 fixe, X suit le coureur (lissée, module pur), Y figée.
  // FIGÉE hors marche (suspens / carte / boutique) : la scène gelée ne bouge pas sous la révélation.
  const cam: MineCam = { x: 0, y: MINE_CAM_Y, zoom: MINE_ZOOM }
  let lastDraw = performance.now()

  // --- entrées (fusion clavier + tactile, comme solo) ---
  const kb = createKeyboard()
  kb.attach()
  const touch = isTouch ? createTouchControls({
    onPause() { onBack() },                                   // pas de pause dans la Mine : ⏸ = retour
    onMute() { toggleMute(); touch!.setMuted(isMuted()) },
  }) : null
  touch?.attach()
  touch?.setMuted(isMuted())
  function mergedInput(): PlayerInput {
    const a = kb.current()
    if (!touch) return a
    const b = touch.current()
    return {
      left: a.left || b.left, right: a.right || b.right, up: a.up || b.up, down: a.down || b.down,
      digL: a.digL || b.digL, digR: a.digR || b.digR, use: a.use || b.use, dash: a.dash || b.dash,
      useDir: a.useDir ?? b.useDir,
    }
  }

  // --- état de scène ---
  type Phase = 'walking' | 'suspense' | 'card' | 'shop'
  let phase: Phase = 'walking'
  let cracking = false            // un apiCrack() en vol : ignore les déclenchements concurrents
  // Suspens de creuse (Lot 3) : deux horloges murales — start (coup de pioche : fissures, punch)
  // et rarityAt (réponse serveur : paliers, éclatement). errAt = échec réseau (courte extinction).
  interface Susp { start: number; cell: number; res: CrackResponse | null; rarityAt: number | null; errAt: number | null; lastStage: number; lastPalier: number; lastHit: number; impDone: boolean }
  let susp: Susp | null = null
  // Pluie d'or (Mine fun, lot C) : spectacle interactif d'un sac de pièces DÉJÀ crédité par le
  // serveur — les pièces jaillissent du trou et se ramassent en courant (~10 s), rien n'est perdu.
  interface Rain { coins: RainCoin[]; got: number; total: number; startedAt: number; combo: number; floorY: number }
  let rain: Rain | null = null
  let done = false
  let wallet: WalletDto | null = null
  let pity: PityDto | null = null // jauge de chance : compteurs « avant garanti » (lecture seule)
  let owned = new Set<string>()   // loot_ids possédés (pour filtrer la boutique d'éclats)
  let equippedPickaxe: string | undefined // slot pickaxe équipé (lu de /state) : variante de son de casse
  let overlay: HTMLElement | null = null // carte de révélation OU boutique en cours (DOM)
  let equipUi: { close(): void } | null = null // le vestiaire : overlay DOM autonome
  let loop: LoopHandle
  let lastNoBrickToast = 0        // anti-spam du toast « plus de briques » (creusage à sec)
  // Bandeau d'arrivée d'étage (profondeur) : affiché ~1,6 s à chaque changement d'étage.
  let lastDepth: 1 | 2 | 3 = 1
  let depthBanner: { depth: number; at: number } | null = null
  const BANNER_MS = 1600

  // Anim de pioche (transitoire, cosmétique) : par coureur, tick jusqu'auquel jouer la frame `dig`.
  // Alimenté sur l'event digAnim (comme solo) -> render(...digUntil...) montre le coup de pioche.
  const digUntil: number[] = []

  const errText = (e: unknown): string => (e instanceof Error && e.message ? e.message : 'Une erreur est survenue.')

  /** Recharge wallet + inventaire (le seul endroit qui met `owned` à jour). Tire-et-oublie :
   *  hors ligne / non connecté, on garde l'état courant sans jamais bloquer la scène. */
  function refreshState(): void {
    apiMineState()
      .then((s) => { if (!done) { wallet = s.wallet; owned = new Set(s.inventory); equippedPickaxe = s.equipped?.pickaxe; pity = s.pity } })
      .catch(() => { /* hors ligne / non connecté : silencieux */ })
  }

  function freeze(): void { state.finished = true }
  function resumeWalk(): void { phase = 'walking'; state.finished = false }
  function closeOverlay(): void { overlay?.remove(); overlay = null }

  // --- cinématique de la GRANDE RÉVÉLATION (HORS sim) : pilotée par la boucle de dessin sur
  // l'horloge murale (performance.now). Bornes en TEMPS (module mine-crack) -> une image sautée ne
  // décale rien ; la carte de révélation + la fanfare tombent toujours à CARD_MS via un setTimeout. ---
  interface Cine {
    start: number            // performance.now() au lancement
    res: CrackResponse
    ox: number; oy: number   // centre écran (px monde) de la case creusée : origine de la montée
    rays: string[]           // couleurs des rayons de lumière (rareté ; méga = arc-en-ciel)
    shardColors: string[]    // couleurs des particules
    base: string             // teinte dominante (halo + anneaux)
    mega: boolean
    spr: SpriteRef           // sprite 16px (feuille principale) du loot présenté
    burst: boolean           // le burst de particules du « pop » a été émis une fois
    lastSpark: number        // dernière émission du filet de particules (performance.now)
    petId?: string           // familier gagné : peint par sa SILHOUETTE, comme en jeu (06/08)
    // TENUE / ACCESSOIRE (26/07) : ils n'ont pas de sprite — ils SE PORTENT. On révèle donc le
    // HÉROS qui l'endosse, comme dans le vestiaire. Sans ça, les deux tenues méga et la couronne
    // (les tirages les plus rares du jeu) se révélaient en brique mystère générique.
    porte?: { outfit: string; hat: string }
  }
  let cine: Cine | null = null
  let cineTimer: ReturnType<typeof setTimeout> | null = null

  // Particules de la révélation : petit émetteur balistique LOCAL. Positions en px écran. Horloge = ms.
  interface Shard { x: number; y: number; vx: number; vy: number; born: number; life: number; color: string; rot: number; vr: number; sz: number; ground: number | null; bounces: number; last: number; world: boolean }
  let shards: Shard[] = []
  // Trou béant après l'éclatement : bord incandescent qui REFROIDIT (~2,2 s), couleur de la rareté.
  let holeGlow: { x: number; y: number; at: number; color: string } | null = null

  const BIG = 176            // taille du loot présenté (px), blité en NEAREST-NEIGHBOR
  const TX = canvas.width / 2
  const TY = ROWS * TS * 0.4 // ~307 px : centre haut-milieu (laisse la carte DOM en bas)

  // Sprite « héros » du loot pour la révélation : un sprite dédié (pièces / pioches / familiers /
  // statue) ; défaut = la brique mystère dorée (titres / traînées / victoires / consommables).
  function heroOf(res: CrackResponse): SpriteRef {
    if (res.loot.type === 'coins') return GOLD
    if (res.loot.id === 'pioche-marteau') return PIOCHE_MARTEAU[0]
    if (res.loot.id === 'pioche-foreuse') return PIOCHE_FOREUSE[0]
    // ⚠️ PAS DE BRANCHE FAMILIER ICI, ET C'EST VOULU (11/08). Elle lisait `PET_SPRITES`, la feuille
    // 16 px restée à dix compagnons — le même piège qui a rendu trois familiers invisibles en jeu.
    // Son résultat n'était de toute façon jamais affiché : `drawHeroSprite` peint tout loot
    // `pet-*` par sa SILHOUETTE (branche `c.petId`), qui existe pour les treize. Retiré plutôt que
    // laissé dormant : la prochaine bête ajoutée n'aura pas à y penser.
    if (res.loot.id === 'deco-statue') return DECO_STATUE
    return BRIQUE_MYSTERE
  }

  /** '#rrggbb' + alpha -> 'rgba(...)' (stops de dégradés / voiles teintés). */
  function hexA(hex: string, a: number): string {
    const h = hex.replace('#', '')
    const r = parseInt(h.slice(0, 2), 16), g = parseInt(h.slice(2, 4), 16), b = parseInt(h.slice(4, 6), 16)
    return `rgba(${r},${g},${b},${Math.max(0, Math.min(1, a))})`
  }

  // --- casse d'une brique du sol : gèle, lance le SUSPENS (fissures tout de suite : la latence
  // réseau est invisible) + la casse API en parallèle. drawSuspense fait éclater la brique quand la
  // timeline pure le dit, puis enchaîne la grande révélation. Échec réseau : extinction + rebouche. ---
  function doCrack(cell: number, depth: 1 | 2 | 3): void {
    if (cracking || susp) return
    cracking = true
    phase = 'suspense'
    freeze()
    susp = { start: performance.now(), cell, res: null, rarityAt: null, errAt: null, lastStage: 0, lastPalier: -1, lastHit: 0, impDone: false }
    mineAudioUnlock() // le creusage vient d'une touche : le geste débloque le contexte audio Mine
    rumbleStart(110 - 25 * (depth - 1)) // le roulement gronde — plus GRAVE en profondeur
    // éclats de pierre immédiats du coup de pioche (screen-space, via la caméra figée)
    spawnShards(colOf(cell, state.cols) * 48 + 24, rowOf(cell, state.cols) * 48 + 6, ['#3a2a1c', '#6b5a3a', '#2a1c12'], 10, 0.22, null, true)
    apiCrack(depth)
      .then((res) => {
        cracking = false
        if (done || !susp) return
        wallet = res.wallet
        pity = res.pity // la jauge de chance suit chaque casse
        refreshState() // un cosmétique neuf entre en inventaire : la boutique d'éclats doit le savoir
        susp.res = res
        susp.rarityAt = performance.now() // les paliers de rareté démarrent ICI
      })
      .catch((e) => {
        cracking = false
        if (done || !susp) return
        susp.errAt = performance.now()
        showToast(errText(e))
      })
  }

  /** Le tap/clic/Espace pendant le suspens saute à l'éclatement (anti-lassitude à la chaîne). */
  function trySkip(): void {
    mineAudioUnlock() // tout tap débloque aussi le contexte audio de la Mine
    if (!susp || susp.errAt !== null || !susp.res || susp.rarityAt === null) return
    const target = suspenseSkipTR(susp.res.rarity)
    if (performance.now() - susp.rarityAt < target) susp.rarityAt = performance.now() - target
  }

  /** La phase 'suspense', frame par frame : caméra punchée vers la case (zoom + glissement 25 % +
   *  séisme), scène + fissures/lueur, puis à l'heure dite : morceaux de brique + grande révélation. */
  function drawSuspense(prev: GameState | null, cur: GameState, alpha: number): void {
    const s = susp!
    const now = performance.now()
    const clock: SuspenseClock = {
      t: now - s.start,
      tR: s.rarityAt !== null ? now - s.rarityAt : null,
      rarity: s.res ? s.res.rarity : null,
    }
    if (s.errAt !== null) { // échec réseau : courte extinction puis on rebouche et on reprend
      rumbleStop()
      if (now - s.errAt > 250) { susp = null; closeReveal(); return }
    } else if (suspenseOver(clock)) { // ÉCLATEMENT : morceaux balistiques, puis révélation OU pluie
      const res = s.res!, cell = s.cell
      burstBoom(res.rarity)
      setMineDrama(0, 0, 0)
      // v2/v3 : morceaux en MONDE qui REBONDISSENT sur la ligne du sol (rotation + poussière au contact)
      spawnShards(colOf(cell, state.cols) * 48 + 24, rowOf(cell, state.cols) * 48 + 24,
        ['#3a2a1c', '#2a1c12', '#ffd23f', ...rarityShards(res.rarity)], 30, 0.4, rowOf(cell, state.cols) * 48 + 4, true)
      holeGlow = { x: colOf(cell, state.cols) * 48 + 24, y: rowOf(cell, state.cols) * 48 + 24, at: now, color: rarityColor(res.rarity) } // bord incandescent
      susp = null
      if (res.loot.type === 'coins' && res.coinsGranted > 0) {
        startRain(res, cell) // sac de pièces : PLUIE D'OR jouable, pas de carte plate
      } else {
        phase = 'card'
        playRevelation(res, cell)
      }
      renderMine(ctx, prev, cur, alpha, cam, digUntil, now, null, rain?.coins ?? null, sheet)
      drawHoleGlow(now)
      drawShards(now, cam)
      drawBottomBar()
      return
    }
    // dramaturgie SONORE : le roulement suit la lueur, un tick à chaque palier franchi, et le
    // SILENCE ~140 ms avant l'éclatement (le vide avant le boom)
    if (s.errAt === null) {
      const pal = suspensePalier(clock)
      if (pal > s.lastPalier) {
        for (let n = s.lastPalier + 1; n <= pal; n++) palierTick(n)
        s.lastPalier = pal
      }
      if (clock.tR !== null && s.res && clock.tR >= suspenseBurstAt(s.res.rarity) - 140) rumbleStop()
      else rumbleSet(suspenseGlow(clock).intensity)
    }
    const stage = suspenseCrackStage(clock.t)
    const holeCx = colOf(s.cell, state.cols) * 48 + 24
    const holeTopY = rowOf(s.cell, state.cols) * 48 + 6
    // v2 : TROIS coups de pioche cadencés avant les fissures — son qui monte (le 3e est LOURD) + gravats
    const hits = pickHitsDone(clock.t)
    if (hits > s.lastHit) {
      for (let h = s.lastHit; h < hits; h++) crackStrike(h >= 1, h === 2)
      spawnShards(holeCx, holeTopY, ['#6b5a3a', '#3a2a1c', '#8a7a5a'], 7, 0.2, null, true)
      s.lastHit = hits
    }
    // craquement + poussière à chaque nouveau stade de fissures (le sol qui cède, de plus en plus grave)
    if (stage > s.lastStage) {
      s.lastStage = stage
      mineCreak(stage)
      spawnShards(holeCx, holeTopY, ['#6b5a3a', '#3a2a1c'], 6, 0.16, null, true)
    }
    // implosion : une seule ASPIRATION sonore quand la fenêtre s'ouvre (~400 ms avant le boom)
    if (!s.impDone && implosionP(clock) > 0) { s.impDone = true; implosionWhoosh() }
    // caméra du suspens : fonction PURE multi-étages (fix 21/07 nuit : l'ancien clamp `16*48`
    // jouait l'étage 3 hors écran — « l'animation ne marche qu'à la première ligne »)
    const camS: MineCam = suspensePunchCam(clock, cam, holeCx, holeTopY, { sx: Math.sin(now / 31), sy: Math.cos(now / 37) })
    setMineDrama(Math.min(1, suspenseGlow(clock).intensity + implosionP(clock) * 0.4), holeCx, holeTopY) // la scène participe
    renderMine(ctx, prev, cur, alpha, camS, digUntil, now, null, rain?.coins ?? null, sheet)
    renderSuspense(ctx, clock, holeCx, holeTopY, now, s.cell, camS)
    drawShards(now, camS)
    drawBottomBar()
  }

  /** Démarre la pluie d'or : la sim REPREND (on court ramasser), les pièces jaillissent du trou
   *  — qui reste OUVERT (repousse naturelle : on peut même sauter dedans pendant la pluie). Le
   *  montant est DÉJÀ crédité (CrackResponse) : à l'expiration, les retardataires filent
   *  d'elles-mêmes au compteur — rien n'est jamais perdu. */
  function startRain(res: CrackResponse, cell: number): void {
    resumeWalk()
    const ox = colOf(cell, state.cols) * 48 + 24
    const oy = rowOf(cell, state.cols) * 48 + 18 // depuis l'intérieur du trou, juste sous la surface
    rain = {
      coins: coinRainPlan(res.coinsGranted, cell * 7 + 1, ox, oy),
      got: 0, total: res.coinsGranted, startedAt: performance.now(), combo: 0,
      floorY: rowOf(cell, state.cols) * 48 - 6, // les pièces se posent sur le sol de CET étage
    }
  }

  /** Lance la révélation : fige tout dans un `cine` (rareté / sprite / origine FIGÉS au départ) et
   *  arme la borne dure de la carte. La sim est déjà gelée par doCrack (freeze). */
  function playRevelation(res: CrackResponse, cell: number): void {
    shards = []
    const hero = heroOf(res)
    // Origine de la montée : centre de la case creusée en px ÉCRAN, via la caméra FIGÉE (sim gelée).
    const o = cell >= 0
      ? worldToScreen(colOf(cell, state.cols) * 48 + 24, rowOf(cell, state.cols) * 48 + 24, cam)
      : { x: TX, y: TY }
    const ox = o.x, oy = o.y
    cine = {
      start: performance.now(), res, ox, oy,
      rays: rarityRays(res.rarity), shardColors: rarityShards(res.rarity),
      base: rarityColor(res.rarity), mega: isMega(res.rarity),
      spr: hero, burst: false, lastSpark: 0,
      petId: res.loot.id.startsWith('pet-') ? res.loot.id : undefined, // familier -> sa silhouette
      porte: res.loot.id.startsWith('tenue-') ? { outfit: res.loot.id, hat: '' }
        : res.loot.id.startsWith('chapeau-') ? { outfit: equipped('outfit') ?? '', hat: res.loot.id }
          : undefined,
    }
    if (cineTimer !== null) clearTimeout(cineTimer)
    // Borne DURE (temps, pas frames) : la carte + la fanfare tombent à CARD_MS quoi qu'il arrive.
    // MÉGA : pluie d'étoiles dorées qui retombent et REBONDISSENT au niveau du loot
    if (isMega(res.rarity)) spawnShards(TX, 40, ['#ffd23f', '#fff2b0', '#ffffff'], 16, 0.3, TY + BIG / 2)
    const lootId = res.loot.id // familier si id `pet-*` (le DTO ne porte pas le slot)
    cineTimer = setTimeout(() => {
      cineTimer = null
      if (done || !cine) return // teardown / annulé : ne jamais révéler sur une scène démontée
      crackFanfare(cine.res.rarity)
      if (lootId.startsWith('pet-')) setTimeout(() => { if (!done) petCue(lootId, 'joy') }, 380) // le familier POUSSE SON CRI
      showRevealCard(cine.res)
    }, CARD_MS)
  }

  /** Bord incandescent du trou d'éclatement : lueur de la rareté qui REFROIDIT (~2,2 s). */
  function drawHoleGlow(now: number): void {
    if (!holeGlow) return
    const k = (now - holeGlow.at) / 2200
    if (k >= 1) { holeGlow = null; return }
    const pS = worldToScreen(holeGlow.x, holeGlow.y, cam)
    // les 260 premières ms : FLASH blanc bref + ONDE DE CHOC qui traverse la salle (le « boom » visuel)
    const k2 = (now - holeGlow.at) / 260
    if (k2 < 1) {
      ctx.save()
      ctx.fillStyle = `rgba(255,252,240,${(0.3 * (1 - k2) * (1 - k2)).toFixed(3)})`
      ctx.fillRect(0, 0, canvas.width, canvas.height)
      ctx.globalCompositeOperation = 'lighter'
      ctx.strokeStyle = hexA(holeGlow.color, (1 - k2) * 0.55)
      ctx.lineWidth = 2 + 5 * (1 - k2)
      ctx.beginPath(); ctx.arc(pS.x, pS.y, 30 + k2 * 560, 0, Math.PI * 2); ctx.stroke()
      ctx.strokeStyle = `rgba(255,255,255,${((1 - k2) * 0.3).toFixed(3)})`
      ctx.lineWidth = 1.5
      ctx.beginPath(); ctx.arc(pS.x, pS.y, 18 + k2 * 430, 0, Math.PI * 2); ctx.stroke()
      ctx.restore()
    }
    ctx.save()
    ctx.globalCompositeOperation = 'lighter'
    const r = (30 + k * 10) * cam.zoom / 3
    const g = ctx.createRadialGradient(pS.x, pS.y, 0, pS.x, pS.y, r)
    g.addColorStop(0, hexA(holeGlow.color, 0.5 * (1 - k) * (0.8 + 0.2 * Math.sin(now / 90))))
    g.addColorStop(1, hexA(holeGlow.color, 0))
    ctx.fillStyle = g
    ctx.beginPath(); ctx.arc(pS.x, pS.y, r, 0, Math.PI * 2); ctx.fill()
    ctx.restore()
  }

  /** Émet `n` particules balistiques teintées en éventail vers le haut depuis (cx, cy) px écran.
   *  `power` = vitesse (px/ms). Cosmétique client pur (jamais sim/hash). */
  /** Émet des particules. `world: true` (défaut pour la Mine) = coordonnées MONDE, dessinées sous
   *  la caméra du frame -> elles COLLENT à la scène même quand le punch glisse/zoome (fix décalage
   *  21/07 soir : avant, les gravats étaient des instantanés écran qui dérivaient). `world: false`
   *  = espace écran (la cinématique de révélation, qui vit hors caméra). */
  function spawnShards(cx: number, cy: number, colors: string[], n: number, power: number, ground: number | null = null, world = false): void {
    const now = performance.now()
    const k = world ? 1 / 3 : 1 // les réglages historiques sont en px écran zoom 3
    for (let i = 0; i < n; i++) {
      const a = -Math.PI / 2 + (Math.random() - 0.5) * Math.PI * 1.35 // éventail vers le haut
      const sp = power * (0.5 + Math.random()) * k
      shards.push({
        x: cx, y: cy, vx: Math.cos(a) * sp, vy: Math.sin(a) * sp - power * 0.4 * k,
        born: now, life: ground !== null ? 900 + Math.random() * 500 : 520 + Math.random() * 360,
        color: colors[i % colors.length] ?? '#ffffff',
        rot: Math.random() * Math.PI, vr: (Math.random() - 0.5) * 0.02,
        sz: (3.5 + Math.random() * 2.5) * k, ground, bounces: 0, last: now, world,
      })
    }
    if (shards.length > 360) shards.splice(0, shards.length - 360) // filet anti-emballement
  }

  function drawShards(now: number, camNow?: MineCam): void {
    // particules MONDE : sous la caméra du frame (elles collent à la scène) ; particules ÉCRAN : brutes
    const cw = camNow ?? cam
    ctx.save()
    const g0 = 0.00055 // gravité (px écran/ms²)
    for (let k = shards.length - 1; k >= 0; k--) {
      const s = shards[k]!
      const age = now - s.born
      if (age >= s.life) { shards.splice(k, 1); continue }
      const g = s.world ? g0 / 3 : g0
      ctx.setTransform(1, 0, 0, 1, 0, 0)
      if (s.world) { ctx.scale(cw.zoom, cw.zoom); ctx.translate(-cw.x, -cw.y) }
      // intégration STATEFUL (v2) : nécessaire pour les REBONDS au sol des morceaux de brique
      const dt = Math.min(50, now - s.last)
      s.last = now
      s.vy += g * dt
      s.x += s.vx * dt
      s.y += s.vy * dt
      s.rot += s.vr * dt
      if (s.ground !== null && s.y > s.ground && s.vy > 0 && s.bounces < 2) {
        s.y = s.ground
        s.vy *= -0.45
        s.vx *= 0.75
        s.vr *= 0.7
        s.bounces++
        ctx.globalAlpha = 0.5 // bouffée de poussière au contact
        ctx.fillStyle = '#8a7a6a'
        ctx.fillRect(s.x - 2, s.ground - 1, 4, 2)
      }
      const kk = age / s.life
      const twinkle = 0.65 + 0.35 * Math.sin(age * 0.03 + s.born) // scintillement
      ctx.globalAlpha = Math.max(0, 1 - kk) * twinkle
      ctx.fillStyle = s.color
      const sz = s.sz * (1 - kk * 0.4)
      ctx.save()
      ctx.translate(s.x, s.y)
      ctx.rotate(s.rot)
      ctx.fillRect(-sz / 2, -sz / 2, sz, sz)
      ctx.restore()
    }
    ctx.restore()
    ctx.globalAlpha = 1
  }

  /** Le loot en TRÈS grand (16px blité en nearest-neighbor), flottant + léger balancement. */
  function drawHeroSprite(px: number, py: number, size: number, t: number, present: number, c: Cine): void {
    const half = size / 2
    ctx.save()
    ctx.translate(px, py)
    ctx.rotate(revealSway(t) * present)
    ctx.imageSmoothingEnabled = false
    if (c.porte) {
      // Une tenue ou un accessoire se PORTE : on montre le héros qui l'endosse, à la teinte du
      // joueur. Un chapeau se révèle par-dessus la tenue déjà équipée (on voit sa vraie allure).
      drawBaroudeur(ctx, 'idle', 0.2, 1, -half, -half * 0.92, size, {
        hue: hueIndex(avatarHue()), outfit: c.porte.outfit, hat: c.porte.hat, hatTime: t / 16,
      })
    } else if (c.petId) {
      // GAGNER UN FAMILIER MONTRE LE FAMILIER (06/08). La carte de récompense le tirait de
      // `pets.png`, où les trois derniers n'ont pas de ligne : on ouvrait un coffre mega pour
      // découvrir une carte VIDE. Sa silhouette, elle, existe pour les treize.
      ctx.imageSmoothingEnabled = true
      ctx.save(); ctx.translate(-half, -half)
      blitPet(ctx, c.petId, size)
      ctx.restore()
      ctx.imageSmoothingEnabled = false
    } else ctx.drawImage(sheet, c.spr.sx, c.spr.sy, 16, 16, -half, -half, size, size)
    ctx.restore()
  }

  /** La grande révélation, dessinée par la boucle à la place de la scène normale tant que `cine`
   *  est actif. Sim gelée derrière ; tout est piloté par (now - cine.start). */
  function drawRevelation(prev: GameState | null, cur: GameState, alpha: number): void {
    const c = cine!
    const now = performance.now()
    const t = now - c.start
    const s = revealScale(t)                    // 0..1 (montée)
    const present = Math.min(1, t / GROW_MS)    // fondu d'entrée des effets
    const size = TS + (BIG - TS) * s
    const px = c.ox + (TX - c.ox) * s
    // v2 : le loot JAILLIT du trou en ARC balistique (sommet à mi-montée) avant de se poser au centre
    const arc = Math.sin(Math.PI * present) * 110
    const py = c.oy + (TY - c.oy) * s + floatY(t) * s - arc
    const half = size / 2
    // traînée de rareté pendant l'ascension (étincelles semées sur la trajectoire, throttle 40 ms)
    if (t < GROW_MS && now - c.lastSpark > 40) { spawnShards(px, py + half * 0.4, c.shardColors, 1, 0.06); c.lastSpark = now }

    // 1) scène gelée derrière (coup de pioche encore visible un instant via digUntil), puis
    //    assombrissement quasi-noir + vignette radiale centrée sur le loot.
    renderMine(ctx, prev, cur, alpha, cam, digUntil, now, null, null, sheet)
    drawHoleGlow(now)
    const dark = revealDarken(t)
    ctx.fillStyle = hexA('#06040e', dark)
    ctx.fillRect(0, 0, canvas.width, canvas.height)
    const vg = ctx.createRadialGradient(px, py, size * 0.2, px, py, Math.max(canvas.width, canvas.height) * 0.72)
    vg.addColorStop(0, 'rgba(0,0,0,0)')
    vg.addColorStop(1, hexA('#000000', 0.55 * dark))
    ctx.fillStyle = vg
    ctx.fillRect(0, 0, canvas.width, canvas.height)

    // 2) rayons de lumière de la rareté, en rotation lente (additif).
    ctx.save()
    ctx.globalCompositeOperation = 'lighter'
    ctx.translate(px, py)
    ctx.rotate(raySpin(t))
    const nRays = c.mega ? 18 : 12
    const rayLen = size * (c.mega ? 2.9 : 2.3)
    const w = 0.05
    for (let i = 0; i < nRays; i++) {
      const a = (i / nRays) * Math.PI * 2
      ctx.fillStyle = c.rays[i % c.rays.length]!
      ctx.globalAlpha = (c.mega ? 0.13 : 0.1) * present
      ctx.beginPath()
      ctx.moveTo(0, 0)
      ctx.lineTo(Math.cos(a - w) * rayLen, Math.sin(a - w) * rayLen)
      ctx.lineTo(Math.cos(a + w) * rayLen, Math.sin(a + w) * rayLen)
      ctx.closePath()
      ctx.fill()
    }
    ctx.restore()
    // v2 : 2e couche de rayons CONTRE-ROTATIVE, plus longue et plus douce (profondeur de la lumière)
    ctx.save()
    ctx.globalCompositeOperation = 'lighter'
    ctx.translate(px, py)
    ctx.rotate(-raySpin(t) * 0.7)
    for (let i = 0; i < (c.mega ? 12 : 8); i++) {
      const a = (i / (c.mega ? 12 : 8)) * Math.PI * 2 + 0.26
      ctx.fillStyle = c.rays[(i + 1) % c.rays.length]!
      ctx.globalAlpha = (c.mega ? 0.07 : 0.055) * present
      ctx.beginPath()
      ctx.moveTo(0, 0)
      ctx.lineTo(Math.cos(a - 0.035) * rayLen * 1.3, Math.sin(a - 0.035) * rayLen * 1.3)
      ctx.lineTo(Math.cos(a + 0.035) * rayLen * 1.3, Math.sin(a + 0.035) * rayLen * 1.3)
      ctx.closePath()
      ctx.fill()
    }
    // MÉGA : colonne de lumière verticale qui s'estompe après le pop
    if (c.mega && t < GROW_MS + 900) {
      const ka = Math.max(0, 1 - Math.max(0, t - GROW_MS) / 900)
      const colG = ctx.createLinearGradient(0, -canvas.height, 0, canvas.height)
      colG.addColorStop(0, hexA('#fff2b0', 0))
      colG.addColorStop(0.5, hexA('#ffd23f', 0.3 * ka * present))
      colG.addColorStop(1, hexA('#fff2b0', 0))
      ctx.rotate(raySpin(t) * 0.7) // annule la contre-rotation : colonne bien VERTICALE
      ctx.fillStyle = colG
      ctx.fillRect(-size * 0.55, -canvas.height, size * 1.1, canvas.height * 2)
    }
    ctx.restore()
    ctx.globalAlpha = 1

    // 3) halo doux (disque) + anneaux concentriques pulsés derrière le loot.
    const glow = ctx.createRadialGradient(px, py, 0, px, py, size * 1.3)
    glow.addColorStop(0, hexA(c.base, 0.5 * present))
    glow.addColorStop(1, hexA(c.base, 0))
    ctx.fillStyle = glow
    ctx.fillRect(px - size * 1.3, py - size * 1.3, size * 2.6, size * 2.6)
    for (let k = 0; k < 3; k++) {
      const rr = size * (0.68 + k * 0.32) + Math.sin(t / 480 + k) * 5
      ctx.globalAlpha = (0.2 - k * 0.05) * present
      ctx.strokeStyle = c.base
      ctx.lineWidth = 3
      ctx.beginPath()
      ctx.arc(px, py, rr, 0, Math.PI * 2)
      ctx.stroke()
    }
    ctx.globalAlpha = 1

    // 4) le loot en TRÈS grand.
    drawHeroSprite(px, py, size, t, present, c)

    // 5) particules de rareté : burst unique au « pop », puis filet montant + scintillant.
    if (t >= GROW_MS && !c.burst) { spawnShards(px, py + half * 0.3, c.shardColors, c.mega ? 34 : 20, 0.34); c.burst = true; c.lastSpark = now }
    if (t >= GROW_MS && now - c.lastSpark > 110) { spawnShards(px + (Math.random() - 0.5) * size, py + half * 0.5, c.shardColors, c.mega ? 3 : 2, 0.12); c.lastSpark = now }
    drawShards(now)

    // 6) MÉGA : scintillement doré pulsé aux bords (spectacle supplémentaire).
    if (c.mega) {
      const pulse = 0.5 + 0.5 * Math.sin(t / 210)
      ctx.globalAlpha = 0.12 * present * pulse
      const eg = ctx.createRadialGradient(px, py, canvas.width * 0.22, px, py, canvas.width * 0.72)
      eg.addColorStop(0, 'rgba(255,210,63,0)')
      eg.addColorStop(1, 'rgba(255,210,63,0.85)')
      ctx.fillStyle = eg
      ctx.fillRect(0, 0, canvas.width, canvas.height)
      ctx.globalAlpha = 1
    }

    // 7) flash blanc bref au pop.
    const flash = revealFlash(t)
    if (flash > 0) { ctx.fillStyle = hexA('#ffffff', flash); ctx.fillRect(0, 0, canvas.width, canvas.height) }
  }

  /** Coupe la révélation en vol, ferme la carte et reprend la marche. PROFONDEUR (21/07) : le
   *  trou n'est PLUS rescellé ici — la repousse naturelle du moteur (REGROW_TICKS, gelée pendant
   *  suspens/carte) le referme, laissant ~9 s pour SAUTER dedans et descendre d'un étage. */
  function closeReveal(): void {
    if (cineTimer !== null) { clearTimeout(cineTimer); cineTimer = null }
    susp = null // un suspens en vol s'arrête net (échec réseau / teardown)
    rumbleStop()
    cine = null
    shards = []
    closeOverlay()
    resumeWalk()
  }

  /** Filet de sécurité (la Mine n'a pas d'UX de mort) : un coureur enseveli par la repousse d'un
   *  trou creusé à sec (0 brique) réapparaît au départ, drapeaux remis à neuf. */
  function reviveRunner(r: Runner): void {
    const c = centerOf(r.spawnCell, state.cols)
    r.alive = true; r.x = c.x; r.y = c.y
    r.falling = false; r.onLadder = false; r.onRope = false; r.digHeld = false
  }

  function onTick(events: GameEvent[]): void {
    // Anim de pioche : arme la frame `dig` de CE coureur pour DIG_ANIM_TICKS ticks (comme solo).
    for (const e of events) if (e.type === 'digAnim') digUntil[e.runner] = state.tick + DIG_ANIM_TICKS
    // Filet de sécurité : ranime le coureur s'il a été enseveli (trou 0-brique repoussé).
    const r0 = state.runners[0]
    if (r0 && !r0.alive) reviveRunner(r0)
    // DASH LIBRE DANS LA MINE (26/07, demande Claude-Alain « pas de CD sur le dash dans la mine ») :
    // on rearme la recharge à CHAQUE tick. Ici, le dash n'est pas une ressource tactique — il n'y a
    // ni moine, ni mort, ni chrono : c'est un plaisir de déplacement, et l'attente de 8 s ne
    // protégeait rien. Fait depuis le CONTRÔLEUR, hors simulation, exactement comme reviveRunner :
    // le moteur (shared/sim) et le hash d'état restent intacts, donc ni les scores, ni les
    // fantômes, ni le multi ne bougent. La sim empêche d'elle-même de redasher PENDANT un dash
    // (condition `dashUntil <= tick`) : le geste garde sa charge, sa fente et son freinage.
    if (r0) r0.dashReadyAt = state.tick

    if (phase !== 'walking' || cracking) return
    // Creusage RÉUSSI du coureur (index 0) = une brique du sol vient d'être cassée.
    if (!events.some((e) => e.type === 'digAnim' && e.runner === 0)) return
    const digE = events.find((e): e is Extract<GameEvent, { type: 'dig' }> => e.type === 'dig')
    const cell = digE ? digE.cell : -1
    // Précondition : bricks > 0. On ne bloque QUE si le stock est connu ET vide (wallet inconnu /
    // hors ligne : on tente, le serveur reste l'autorité). Sinon : le trou s'ouvre normalement
    // (inoffensif, repousse tout seul) + un toast anti-spam.
    // Profondeur : l'étage de la CASE creusée décide du coût (1/2/3 🧱) et du multiplicateur.
    const depth = cell >= 0 ? depthOfRow(rowOf(cell, state.cols)) : 1
    const cost = DEPTH_COST[depth - 1]!
    // BRIQUE D'OR (25/07) : elle remplace le coût en briques ordinaires, quelle que soit la
    // profondeur — donc tant qu'il en reste une, on peut toujours creuser.
    if (wallet && wallet.goldenBricks <= 0 && wallet.bricks < cost) {
      const now = performance.now()
      if (now - lastNoBrickToast > 2500) {
        showToast(cost > 1 ? `Il faut ${cost} 🧱 pour creuser à l'étage ${depth}` : 'Plus de briques — achète-en à la Boutique')
        lastNoBrickToast = now
      }
      return
    }
    crackStrike(pickaxeVariantOf(equippedPickaxe) === 'marteau', true) // impact de casse (audio existant)
    doCrack(cell, depth)
  }

  function draw(prev: GameState | null, cur: GameState, alpha: number): void {
    const now = performance.now()
    const dt = Math.min(now - lastDraw, 100)
    lastDraw = now
    // Caméra : suit le centre du coureur en marche ; FIGÉE sinon (suspens / carte / boutique).
    // En Y elle PLONGE d'étage en étage (profondeur), avec un lissage plus lent (dramatique).
    const r0 = cur.runners[0]
    if (r0 && phase === 'walking') {
      const focus = (r0.x + SUB / 2) * (48 / SUB) // centre du coureur, px monde
      cam.x = mineCamStep(cam.x, mineCamTargetX(focus), dt)
      const depth = depthOfRow(Math.round(r0.y / SUB))
      cam.y = mineCamStep(cam.y, camYForDepth(depth), dt * 0.45)
      if (depth !== lastDepth) { lastDepth = depth; depthBanner = { depth, at: now } } // bandeau d'étage
    }
    if (susp) { drawSuspense(prev, cur, alpha); return } // suspens de creuse : caméra + fissures
    if (cine) { drawRevelation(prev, cur, alpha); return } // révélation : remplace la scène normale
    // Pluie d'or en cours : physique + ramassage par proximité (module pur), ping à chaque prise
    if (rain) {
      const rcx = r0 ? (r0.x + SUB / 2) * (48 / SUB) : -10000
      const rcy = r0 ? (r0.y + SUB / 2) * (48 / SUB) : -10000
      for (const i of coinRainStep(rain.coins, rcx, rcy, dt, rain.floorY)) {
        rain.got += rain.coins[i]!.value
        rain.combo++
        coinPing(rain.combo)
      }
      if (rain.coins.every((c) => c.taken) || now - rain.startedAt > 10_000) {
        showToast(`+${rain.total} 🪙 ramassées !`) // déjà créditées par le serveur : rien de perdu
        rain = null
      }
    }
    // L'invite affiche le COÛT de l'étage visé (le sol juste sous les pieds du coureur) ET, depuis
    // le 26/07, CE QU'ON Y TROUVE : payer 3 briques doit se lire comme un choix, pas comme une taxe.
    const hintDepth = r0 ? depthOfRow(Math.round(r0.y / SUB) + 1) : 1
    const hintCost = DEPTH_COST[hintDepth - 1]!
    const hint = phase === 'walking' && !rain
      ? { hasBricks: wallet ? wallet.bricks >= hintCost : true, touch: isTouch, cost: hintCost, depth: hintDepth }
      : null
    setMineDrama(0, 0, 0) // hors suspense : la scène respire normalement
    renderMine(ctx, prev, cur, alpha, cam, digUntil, now, hint, rain?.coins ?? null, sheet)
    drawHoleGlow(now)
    if (rain && r0) { // compteur flottant du butin, au-dessus du coureur (à la place de l'invite)
      const p = worldToScreen((r0.x + SUB / 2) * (48 / SUB), r0.y * (48 / SUB), cam)
      ctx.font = 'bold 24px ui-monospace, Menlo, monospace'
      ctx.textAlign = 'center'
      ctx.textBaseline = 'alphabetic'
      ctx.fillStyle = '#ffd23f'
      ctx.fillText(`+${rain.got} 🪙`, p.x, p.y - 58)
    }
    if (depthBanner) { // arrivée d'étage : « ÉTAGE 2 — gains ×2 » en fondu
      const k = (now - depthBanner.at) / BANNER_MS
      if (k >= 1) depthBanner = null
      else renderDepthBanner(ctx, depthBanner.depth, k)
    }
    drawBottomBar()
  }

  function drawBottomBar(): void {
    renderMineHud(ctx, wallet, titleLabel(equipped('title')), isTouch, pity, performance.now())
  }

  // --- overlays DOM (carte de révélation, boutique) : mêmes teintes que le vestiaire/Réglages ---
  function makeOverlay(): { root: HTMLElement; panel: HTMLElement } {
    const root = document.createElement('div')
    root.style.cssText = 'position:fixed;inset:0;z-index:70;background:rgba(8,6,20,.8);display:flex;align-items:center;justify-content:center;padding:14px;font-family:ui-monospace,Menlo,monospace'
    const panel = document.createElement('div')
    panel.style.cssText = 'background:#161230;border:1px solid #35305e;border-radius:14px;padding:18px;max-width:440px;width:100%;max-height:92vh;overflow:auto;color:#f0e9d2;box-sizing:border-box'
    root.appendChild(panel)
    return { root, panel }
  }
  function mkBtn(label: string, onClick: () => void, primary = false): HTMLButtonElement {
    const b = document.createElement('button')
    b.type = 'button'
    b.textContent = label
    b.style.cssText = `display:block;width:100%;margin-top:8px;padding:11px;border-radius:10px;border:0;font-weight:700;font-size:15px;cursor:pointer;font-family:inherit;${primary ? 'background:#ffd23f;color:#221b04' : 'background:#2a2450;color:#f0e9d2'}`
    b.addEventListener('click', onClick)
    return b
  }

  /** Carte détaillée de la révélation : ancrée EN BAS (le loot flotte au-dessus dans le canvas),
   *  fond translucide teinté rareté. Bandeau rareté (méga = « MÉGA SUPER RARE » doré), nom, desc,
   *  pièces / doublon, « Continuer ». Un tap hors carte ferme aussi. */
  function showRevealCard(res: CrackResponse): void {
    closeOverlay()
    const col = RARITY_COLOR[res.rarity] ?? '#f0e9d2'
    const root = document.createElement('div')
    root.style.cssText = 'position:fixed;inset:0;z-index:70;display:flex;align-items:flex-end;justify-content:center;padding:0 14px 26px;font-family:ui-monospace,Menlo,monospace;cursor:pointer'
    const panel = document.createElement('div')
    panel.style.cssText = `background:rgba(18,14,40,.92);border:1px solid ${col}66;border-radius:16px;padding:16px 20px 18px;max-width:440px;width:100%;color:#f0e9d2;box-sizing:border-box;text-align:center;cursor:default;box-shadow:0 0 40px ${col}44,0 12px 34px rgba(0,0,0,.5)`
    panel.addEventListener('click', (e) => e.stopPropagation()) // clic DANS la carte ne ferme pas
    const banner = document.createElement('div')
    banner.textContent = res.rarity === 'mega' ? 'MÉGA SUPER RARE' : (RARITY_LABEL[res.rarity] ?? res.rarity).toUpperCase()
    banner.style.cssText = `font-size:13px;font-weight:800;letter-spacing:.18em;color:${col};margin-bottom:6px${res.rarity === 'mega' ? `;text-shadow:0 0 14px ${col}` : ''}`
    const name = document.createElement('div')
    name.textContent = res.loot.name
    name.style.cssText = `font-size:26px;font-weight:800;line-height:1.15;color:${col};margin-bottom:6px`
    const desc = document.createElement('div')
    desc.textContent = res.loot.desc
    desc.style.cssText = 'font-size:14px;color:#cbc4e0;line-height:1.45'
    panel.append(banner, name, desc)
    if (res.coinsGranted > 0) {
      const c = document.createElement('div')
      c.textContent = `+${res.coinsGranted} 🪙 pièces`
      c.style.cssText = 'margin-top:10px;font-size:16px;font-weight:700;color:#ffd23f'
      panel.appendChild(c)
    }
    if (res.duplicate) {
      const d = document.createElement('div')
      d.textContent = `Doublon → +${res.shards} 💎 éclats`
      d.style.cssText = 'margin-top:8px;font-size:15px;font-weight:700;color:#5db4ff'
      panel.appendChild(d)
    }
    const cont = mkBtn('Continuer', () => closeReveal(), true)
    cont.style.marginTop = '14px'
    panel.appendChild(cont)
    const bricksLeft = res.wallet.bricks ?? 0
    const goldLeft = res.wallet.goldenBricks ?? 0
    const hint = document.createElement('div')
    hint.textContent = goldLeft > 0
      ? `Encore ${goldLeft} ✨ brique${goldLeft > 1 ? 's' : ''} d'or — épique garanti !`
      : bricksLeft > 0 ? `Encore ${bricksLeft} 🧱 — creuse une autre case !` : 'Plus de briques — passe par la Boutique.'
    hint.style.cssText = 'margin-top:10px;font-size:12px;color:#9a93c0'
    panel.appendChild(hint)
    root.appendChild(panel)
    root.addEventListener('click', () => closeReveal()) // tap hors carte ferme
    document.body.appendChild(root)
    overlay = root
  }

  function showShop(): void {
    if (phase !== 'walking') return
    phase = 'shop'
    freeze()
    closeOverlay()
    const { root, panel } = makeOverlay()
    const title = document.createElement('div')
    title.textContent = 'Boutique de la Mine'
    title.style.cssText = 'font-size:20px;font-weight:800;color:#ffd23f;margin-bottom:10px'
    const walletLine = document.createElement('div')
    walletLine.style.cssText = 'font-size:15px;margin-bottom:12px;color:#f0e9d2'
    const body = document.createElement('div') // reconstruit à chaque achat (solde + liste à jour)
    panel.append(title, walletLine, body)

    function renderBody(): void {
      walletLine.textContent = wallet
        ? `🪙 ${wallet.coins}    🧱 ${wallet.bricks}${wallet.goldenBricks > 0 ? `    ✨ ${wallet.goldenBricks}` : ''}    💎 ${wallet.shards}`
        : 'Connecte-toi pour acheter.'
      body.textContent = ''
      body.appendChild(mkBtn(`Acheter une brique (${BRICK_COST} 🪙)`, () => {
        apiBuyBricks(1)
          .then((r) => { wallet = r.wallet; refreshState(); renderBody() })
          .catch((e) => showToast(errText(e)))
      }, true))
      const shardTitle = document.createElement('div')
      shardTitle.textContent = 'Boutique d\'éclats'
      shardTitle.style.cssText = 'margin:16px 0 6px;font-size:15px;font-weight:700;color:#9a93c0'
      body.appendChild(shardTitle)
      const buyable = LOOTS.filter((l) => l.type === 'cosmetic' && !owned.has(l.id))
      if (buyable.length === 0) {
        const none = document.createElement('div')
        none.textContent = owned.size > 0 ? 'Tous les cosmétiques débloqués !' : 'Casse des briques pour débloquer des cosmétiques.'
        none.style.cssText = 'font-size:13px;color:#cbc4e0'
        body.appendChild(none)
      }
      for (const l of buyable) {
        const cost = SHARD_COST[l.rarity]
        const row = document.createElement('div')
        row.style.cssText = 'display:flex;align-items:center;gap:8px;margin-top:8px'
        const info = document.createElement('div')
        info.style.cssText = 'flex:1;min-width:0'
        const n = document.createElement('div')
        n.textContent = l.name
        n.style.cssText = `font-size:14px;font-weight:700;color:${RARITY_COLOR[l.rarity] ?? '#f0e9d2'};white-space:nowrap;overflow:hidden;text-overflow:ellipsis`
        const rl = document.createElement('div')
        rl.textContent = `${RARITY_LABEL[l.rarity] ?? l.rarity}`
        rl.style.cssText = 'font-size:12px;color:#9a93c0'
        info.append(n, rl)
        const buy = document.createElement('button')
        buy.type = 'button'
        buy.textContent = `${cost} 💎`
        buy.style.cssText = 'flex:0 0 auto;padding:8px 12px;border-radius:9px;border:0;background:#2a2450;color:#f0e9d2;font-weight:700;font-size:14px;cursor:pointer;font-family:inherit'
        buy.addEventListener('click', () => {
          apiShardBuy(l.id)
            .then((r) => { wallet = r.wallet; owned.add(l.id); refreshState(); renderBody() })
            .catch((e) => showToast(errText(e)))
        })
        row.append(info, buy)
        body.appendChild(row)
      }
      body.appendChild(mkBtn('Fermer', () => { closeOverlay(); resumeWalk() }))
    }
    renderBody()
    document.body.appendChild(root)
    overlay = root
    refreshState() // solde/inventaire frais à l'ouverture
    setTimeout(() => { if (!done && phase === 'shop' && overlay === root) renderBody() }, 300)
  }

  // --- LE VESTIAIRE : overlay DOM autonome (vestiaire-ui). Comme la boutique, il gèle la scène ;
  // il gère lui-même son DOM et prévient via onClose (on reprend la marche). Depuis le 26/07 c'est
  // le MÊME écran qu'à l'accueil : le héros en grand, ses couleurs, ses tenues, son familier. ---
  function showEquip(): void {
    if (phase !== 'walking') return
    phase = 'shop' // réutilise la phase « non jouable » (sim gelée) de la boutique
    freeze()
    closeOverlay()
    equipUi = startVestiaire({
      onClose: () => { equipUi = null; if (!done) resumeWalk() },
      onChange: () => { /* le titre de la barre du bas relit equipped() chaque frame : rien à faire */ },
    })
  }

  // --- barre d'outils DOM (coin haut-gauche) : Boutique + Équipement + Retour, hors du canvas ---
  const toolbar = document.createElement('div')
  toolbar.style.cssText = 'position:fixed;top:10px;left:10px;z-index:55;display:flex;gap:8px;font-family:ui-monospace,Menlo,monospace'
  const tbBtn = (label: string, onClick: () => void): HTMLButtonElement => {
    const b = document.createElement('button')
    b.type = 'button'; b.textContent = label
    b.style.cssText = 'padding:9px 13px;border-radius:10px;border:1px solid #35305e;background:rgba(22,18,48,.9);color:#f0e9d2;font-weight:700;font-size:14px;cursor:pointer;font-family:inherit'
    b.addEventListener('click', onClick)
    return b
  }
  toolbar.append(tbBtn('🛒 Boutique', () => showShop()), tbBtn('🎽 Vestiaire', () => showEquip()), tbBtn('😄 Emote', () => { if (phase === 'walking') openEmoteWheel() }), tbBtn('← Retour', () => onBack()))

  // Échap / ⏸ / « Retour » : ferme d'abord un overlay ouvert (carte / boutique), sinon quitte.
  function onBack(): void {
    if (equipUi) { equipUi.close(); return } // close() -> onClose -> resumeWalk (écran d'équipement)
    if (susp) return // suspens en cours (<= 2,7 s) : on laisse la brique éclater
    if (cine || overlay) { closeReveal(); return } // révélation en cours / carte / boutique : ferme + rebouche
    quit()
  }
  function onKey(e: KeyboardEvent): void {
    mineAudioUnlock() // tout geste clavier débloque le contexte audio de la Mine
    // EMOTES (26/07) : E ouvre la roue, 1 à 8 jouent directement. Seulement pendant la MARCHE :
    // sous la révélation ou dans la boutique, Échap et Espace gardent leur rôle.
    if (phase === 'walking' && handleEmoteKey(e.code)) { e.preventDefault(); return }
    if (e.code === 'Escape') { e.preventDefault(); onBack() }
    if (susp && (e.code === 'Space' || e.code === 'Enter')) { e.preventDefault(); trySkip() }
  }

  function quit(): void {
    if (done) return
    teardown()
    opts.onQuit()
  }
  function teardown(): void {
    if (done) return
    done = true
    loop.stop()
    kb.detach()
    touch?.detach()
    removeEventListener('keydown', onKey)
    canvas.removeEventListener('pointerdown', trySkip)
    mineAudioTeardown()
    if (cineTimer !== null) { clearTimeout(cineTimer); cineTimer = null } // annule la révélation en vol
    cine = null
    closeOverlay()
    equipUi?.close() // retire l'overlay d'équipement s'il est ouvert (done=true -> pas de resumeWalk)
    closeEmoteWheel() // la roue est un overlay DOM : elle ne survit pas à la scène
    stopEmote()
    toolbar.remove()
  }

  // Démarrage : la boucle d'abord (teardown référence `loop`), puis écouteurs + barre + 1er état.
  loop = startLoop({ state, input: mergedInput, onTick, draw })
  addEventListener('keydown', onKey)
  canvas.addEventListener('pointerdown', trySkip) // tap pendant le suspens = saut à l'éclatement
  document.body.appendChild(toolbar)
  refreshState()

  return { stop: teardown }
}
